import { filterClasses } from "@swivl-mono/helpers";
import { Channel, EscalationStatus } from "@swivl-mono/types";
import { Button, ButtonVariant } from "@swivl-mono/ui";
import { HTMLProps, useMemo } from "react";

import { ChannelColors, ChannelIcons } from "../../../../../Helpers/Conversations/Conversations.Helper";
import useHasPendingUpdates from "../../../../../Hooks/useHasPendingUpdates";
import { SessionMessageData } from "../../../../../Hooks/useSessionMessageData";
import MembersModel from "../../../../../Models/Member/Members.Model";
import { PendingUpdateType } from "../../../../../Models/Pending/Pending.Model";
import { UserConversationsInterface, UsersModel } from "../../../../../Models/Users/Users.Model";
import { UsersOnlineManager } from "../../../../../Models/Users/UsersOnline.Manager";
import { parseTime } from "./parseTime";

interface ConversationCardProps extends HTMLProps<HTMLDivElement> {
  conversation: UserConversationsInterface | undefined;
  sessionMessageData?: { [userId: number]: { [sessionId: number]: SessionMessageData } };
  selectedUserId?: number | undefined;
  className?: string;
  isEscalated?: boolean;
  isOnline?: boolean;
}

export function OpenConversationCard({
  selectedUserId,
  conversation,
  sessionMessageData,
  className,
  isEscalated = false,
  isOnline = false,
  ...rest
}: ConversationCardProps) {
  const isPending = useHasPendingUpdates([{ type: PendingUpdateType.SESSION_ESCALATION, id: conversation?.user?.id || "" }]);
  const membersMap = MembersModel.useState((s) => s.membersMap);

  const { agentName, currentEscalation } = useMemo(() => {
    const escalation = conversation?.escalations
      ? Object.values(conversation.escalations).find(
          (e) => e.status === EscalationStatus.ACTIVE || e.status === EscalationStatus.PENDING || e.status === EscalationStatus.ASSIGNED
        )
      : undefined;
    if (!escalation) {
      return { agentName: "", currentEscalation: undefined };
    }
    const _agentName = escalation?.assignedMemberId ? membersMap?.[escalation?.assignedMemberId]?.agentName || "Unknown Agent" : "Unknown Agent";
    return { agentName: _agentName, currentEscalation: escalation };
  }, [conversation?.escalations, membersMap]);

  const handleArchiveUser = (userId: number | undefined) => {
    if (userId) {
      UsersModel.setArchivedForUser(userId, true);
    }
  };
  const channel = conversation?.lastUserMessageChannel || Channel.WEB;
  const ChannelIcon = ChannelIcons[channel];
  const isSelected = conversation?.user?.id === selectedUserId;

  const timestamp =
    conversation?.lastUserMessageAt || conversation?.user?.lastInteractionAt
      ? parseTime(conversation.lastUserMessageAt ?? conversation?.user?.lastInteractionAt)
      : null;
  const channelLabel = conversation?.lastUserMessageChannel ? ` | ${conversation.lastUserMessageChannel}` : "";

  return (
    <div
      className={filterClasses(
        "group relative flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition-colors duration-200 ease-in-out hover:bg-grey-100",
        isSelected ? "bg-grey-50" : "bg-white",
        className
      )}
      {...rest}
    >
      {/* avatar */}
      <div
        className={filterClasses("relative flex size-8 shrink-0 items-center justify-center rounded-full", `${ChannelColors[channel].bg} text-white`)}
      >
        <ChannelIcon size={16} />
        <div
          className={filterClasses(
            "absolute right-0 bottom-0 size-2 rounded-full outline-2 transition-all duration-200",
            isSelected ? "outline-grey-50" : "outline-white",
            isOnline || UsersOnlineManager.isConversationFresh(conversation?.user?.id)
              ? "border border-teal-500 bg-teal-500"
              : "border border-grey-600 bg-white"
          )}
        />
      </div>

      {/* content */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {/* Row 1: name + timestamp|channel */}
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-semibold text-black">{conversation?.user?.name || conversation?.name}</span>
          {timestamp && (
            <p
              className={filterClasses(
                "shrink-0 text-sm text-grey-600 opacity-100 transition-opacity duration-200 ease-in-out",
                !isEscalated && "group-focus-within:opacity-0 group-hover:opacity-0"
              )}
            >
              {timestamp}
              {channelLabel}
            </p>
          )}
        </div>

        {/* Row 2: last message + agent */}
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm text-grey-600">{conversation?.lastUserMessage}</p>
          {currentEscalation?.assignedMemberId && (
            <div className="flex shrink-0 items-center gap-1 text-sm whitespace-nowrap text-grey-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="11" height="10">
                <title>takeover icon</title>
                <path
                  className="fill-grey-600"
                  d="M9.6000004 0L0.40000001 0C0.17874999 0 0 0.17874999 0 0.40000001L0 9.6000004C0 9.82125 0.17874999 10 0.40000001 10L9.6000004 10C9.82125 10 10 9.82125 10 9.6000004L10 0.40000001C10 0.17874999 9.82125 0 9.6000004 0ZM6.8337498 5.0812502L3.75875 7.3062501C3.6925001 7.3537502 3.5999999 7.3062501 3.5999999 7.2249999L3.5999999 6.6387501C3.5999999 6.51125 3.6612501 6.3899999 3.7650001 6.3150001L5.5825 5L3.7650001 3.6849999C3.6612501 3.6099999 3.5999999 3.49 3.5999999 3.3612499L3.5999999 2.7750001C3.5999999 2.6937499 3.6925001 2.64625 3.75875 2.6937499L6.8337498 4.9187498C6.8887501 4.9587498 6.8887501 5.0412502 6.8337498 5.0812502Z"
                  fillRule="evenodd"
                />
              </svg>
              <p>{agentName}</p>
            </div>
          )}
        </div>
      </div>

      {/* Close button — overlays on hover for non-escalated cards */}
      {!isEscalated && (
        <div
          aria-hidden={true}
          className="pointer-events-none absolute inset-0 z-50 flex items-center justify-end pr-3 opacity-0 transition-opacity duration-200 ease-in-out group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
        >
          <Button
            loading={isPending}
            variant={ButtonVariant.GHOST}
            onClick={(e) => {
              e.preventDefault();
              handleArchiveUser(conversation?.user?.id);
            }}
            onFocus={(e) => e.currentTarget.setAttribute("aria-hidden", "false")}
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
}
